import { GetOperations, Operation } from '@occc-app/offerHelpers/types';
import {
  getCreateLimitOperation,
  getLimitsInConstraintOperations,
  getTransactionFrequencyLimitOperations,
  LimitScope,
  LimitsInConstraintGroupFormValue,
  LimitType,
} from '@occc/feat-limits';

import { ConfigureFormValues } from '../../../types';
import {
  getIsAwardTypeChanged,
  getIsSupportFulfillmentLimits,
  getIsTransactionFrequencyLimitSupportedForAward,
  getIsTransactionLimitSupported,
} from '../utils';

const getFulfillmentLimits = (
  offerId: string,
  values: LimitsInConstraintGroupFormValue,
  isTransactionLimitSupported: boolean,
) => {
  const limitType = LimitType.AwardCount;
  const operations: Operation[] = [];

  if (!values.isEnabled) {
    return operations;
  }

  const globalLimitOperation = getCreateLimitOperation(
    offerId,
    values,
    LimitScope.Global,
    limitType,
  );
  const membershipOperation = getCreateLimitOperation(
    offerId,
    values,
    LimitScope.Membership,
    limitType,
  );
  // Per-transaction AwardCount limits are only supported by Fuel Rollback. When the user
  // switches away to another fulfillment-supporting award type (e.g. Product Discount), a
  // stale `awardCount.transaction.isEnabled = true` from the previous form state must not
  // recreate the unsupported limit after constraint cleanup. Mirrors the gating in
  // getLimitsInConstraintOperations for the regular-update path.
  const transactionOperation = isTransactionLimitSupported
    ? getCreateLimitOperation(offerId, values, LimitScope.Transaction, limitType)
    : null;

  if (globalLimitOperation) {
    operations.push(globalLimitOperation);
  }

  if (membershipOperation) {
    operations.push(membershipOperation);
  }

  if (transactionOperation) {
    operations.push(transactionOperation);
  }

  return operations;
};

export const getLimitsOperations: GetOperations = (
  awardId: string,
  offer,
  values: ConfigureFormValues,
  dirtyFields,
) => {
  if (!offer) {
    return [];
  }

  const operations: Operation[] = [];

  const isAwardTypeChanged = getIsAwardTypeChanged(offer, values);
  const isNewAwardTypeSupportFulfillmentLimits = getIsSupportFulfillmentLimits(values.awardType);

  // On an award-type change, getRequiresVoucherConstraintsDeleteOperations already deletes every
  // existing limit on the offer. If the new award type does not support fulfillment limits at all
  // (e.g. Sweepstakes), there is nothing to recreate and falling through to
  // getLimitsInConstraintOperations would emit duplicate limitDelete ops against the stale
  // offer.limits snapshot (for unsupported transaction and TFL limits).
  if (isAwardTypeChanged && !isNewAwardTypeSupportFulfillmentLimits) {
    return [];
  }

  if (isAwardTypeChanged && isNewAwardTypeSupportFulfillmentLimits) {
    // Fulfillment limits should be created again if needed as we deleted them previously when
    // we had to remove constraint on award type change earlier
    operations.push(
      ...getFulfillmentLimits(
        awardId,
        values.awardCount,
        getIsTransactionLimitSupported(values.awardType),
      ),
    );

    // Transaction Frequency Limits are also wiped during constraint cleanup (see
    // getRequiresVoucherConstraintsDeleteOperations), and on first-create the offer has no
    // limits yet. In both cases we re-emit pure creates by passing [] as the existing limits.
    if (
      getIsTransactionFrequencyLimitSupportedForAward(values.awardType) &&
      values.awardCount?.isEnabled &&
      values.awardCount.transactionFrequency
    ) {
      operations.push(
        ...getTransactionFrequencyLimitOperations(
          awardId,
          values.awardCount.transactionFrequency,
          [],
        ),
      );
    }
  } else if (values.awardCount) {
    operations.push(
      ...getLimitsInConstraintOperations(
        awardId,
        values.awardCount,
        dirtyFields,
        LimitType.AwardCount,
        {
          isTransactionLimitSupported: getIsTransactionLimitSupported(values.awardType),
          isTransactionFrequencyLimitSupported: getIsTransactionFrequencyLimitSupportedForAward(
            values.awardType,
          ),
          offer,
        },
      ),
    );
  }

  return operations;
};
